import { useEffect, useMemo, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { FiFileText, FiDownload, FiPlay, FiClock } from 'react-icons/fi';
import TutorialsSidebarTabs from '../components/TutorialsSidebarTabs';
import TutorialsCardVideo from '../components/TutorialsCardVideo';
import PaginatorButtons from '../../../components/common/Paginator/PaginatorButtons';
import useHasSession from '../../../Hooks/useHasSession';
import { useTempEdgeTranslator } from '../../../Hooks/useTranslation';
import videoData from '../data/videoData.json';
import tutorialTimeEntryPdf from '../pdfs/tutorial-time-entry.pdf';
import './VideosSection.css';

// videoData only ships two catalogues; any other locale falls back to this one.
const DEFAULT_VIDEO_LANGUAGE = 'en';

const VIDEOS_PER_PAGE = 6;

const noop = () => {};

const DOCUMENT_ICON = <FiFileText size={22} />;

const DOWNLOAD_ICON = <FiDownload size={14} />;

const MODE_SWITCH_OPTIONS = [
  { section: 'tutorials', icon: <FiPlay size={16} />, titleId: 'com.tempedge.msg.title.videos' },
  { section: 'documents', icon: <FiFileText size={16} />, titleId: 'com.tempedge.msg.title.documents' }
];

const SIDEBAR_TAB_LABEL_KEYS = {
  employee: 'com.tempedge.msg.title.employee',
  payroll: 'com.tempedge.msg.title.payroll',
  company: 'com.tempedge.msg.title.company',
  user: 'com.tempedge.msg.title.user'
};

// The product name is never translated, so it is the one tab without a dictionary key.
const TEMPEDGE_TAB_LABEL = 'TempEdge';

const EMPLOYEE_DOCUMENTS = [
  {
    id: 'tutorial_time_entry',
    fileType: 'pdf',
    titleId: 'com.tempedge.msg.public.documents.tutorial_time_entry',
    file: tutorialTimeEntryPdf
  }
];

const getColumnsPerRow = () => {
  if (typeof window === 'undefined') return 3;
  if (window.innerWidth < 992) return 2;
  return 3;
};

const getPlaceholderCount = (count, columns) => (count > 0 && count % columns !== 0 ? columns - (count % columns) : 0);

const TutorialsSection = () => {
  const hasSession = useHasSession();
  const { tempedgeTranslator, locale } = useTempEdgeTranslator();
  const selectedLanguage = videoData[locale] ? locale : DEFAULT_VIDEO_LANGUAGE;
  const [activeSection, setActiveSection] = useState('tutorials');
  const [selectedTab, setSelectedTab] = useState('employee');
  const [currentPage, setCurrentPage] = useState(0);
  const [columns, setColumns] = useState(getColumnsPerRow);

  useEffect(() => {
    const handleResize = () => {
      setColumns(getColumnsPerRow());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!hasSession && activeSection === 'documents') {
      setActiveSection('tutorials');
    }
  }, [hasSession, activeSection]);

  const isDocumentsTab = activeSection === 'documents';
  const tabVideos = isDocumentsTab ? [] : videoData[selectedLanguage][selectedTab] || [];
  const totalPages = Math.ceil(tabVideos.length / VIDEOS_PER_PAGE);
  const activePage = totalPages > 0 ? Math.min(currentPage, totalPages - 1) : 0;
  const startIndex = activePage * VIDEOS_PER_PAGE;
  const currentVideos = tabVideos.slice(startIndex, startIndex + VIDEOS_PER_PAGE);

  const handleTabChange = (tabKey) => {
    setSelectedTab(tabKey);
    setCurrentPage(0);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const allSidebarTabs = useMemo(
    () => ({
      ...Object.fromEntries(Object.entries(SIDEBAR_TAB_LABEL_KEYS).map(([tab, labelKey]) => [tab, tempedgeTranslator(labelKey)])),
      tempedge: TEMPEDGE_TAB_LABEL
    }),
    [tempedgeTranslator]
  );

  const sidebarTabs = isDocumentsTab ? { employee: allSidebarTabs.employee } : allSidebarTabs;

  return (
    <div className="tut-page">
      <div className="tut-intro">
        <span className="tut-eyebrow">{tempedgeTranslator('com.tempedge.msg.public.tutorials.eyebrow')}</span>
        <h1 className="tut-headline">{tempedgeTranslator('com.tempedge.msg.public.nav.howitworks')}</h1>
      </div>

      <Container fluid="lg" className="tut-body">
        <Row className="gy-4 align-items-start">
          <Col xs={12} className="tut-sidebar-col">
            <aside className="tut-sidebar">
              {hasSession && (
                <div className="tut-mode-switch" role="tablist">
                  {MODE_SWITCH_OPTIONS.map((option) => (
                    <button
                      key={option.section}
                      type="button"
                      role="tab"
                      aria-selected={activeSection === option.section}
                      title={tempedgeTranslator(option.titleId)}
                      className={`tut-mode-pill ${activeSection === option.section ? 'active' : ''}`}
                      onClick={() => setActiveSection(option.section)}
                    >
                      {option.icon}
                      <span className="tut-mode-label">{tempedgeTranslator(option.titleId)}</span>
                    </button>
                  ))}
                </div>
              )}

              <TutorialsSidebarTabs
                tabs={sidebarTabs}
                activeTab={isDocumentsTab ? 'employee' : selectedTab}
                onSelectTab={isDocumentsTab ? noop : handleTabChange}
              />
            </aside>
          </Col>

          <Col xs={12} className="tut-main-col">
            <main className="tut-content">
              {isDocumentsTab ? (
                <Row className="gx-3 gy-4 tut-video-row">
                  {EMPLOYEE_DOCUMENTS.map((doc) => (
                    <Col key={doc.id} xs={6} sm={4} lg={3}>
                      <div className="tutorials-video-item tutorials-document-item">
                        <div className="tutorials-document-item-top">
                          <div className="tutorials-document-item-thumb">{DOCUMENT_ICON}</div>
                          <span className={`tutorials-document-badge tutorials-document-badge-${doc.fileType}`}>
                            {doc.fileType.toUpperCase()}
                          </span>
                        </div>
                        <p className="video-item-title">{tempedgeTranslator(doc.titleId)}</p>
                        <a className="tutorials-document-item-footer" href={doc.file} download>
                          {DOWNLOAD_ICON}
                          <span>{tempedgeTranslator('com.tempedge.msg.label.download')}</span>
                        </a>
                      </div>
                    </Col>
                  ))}
                </Row>
              ) : (
                <Row className="gx-3 tut-video-row">
                  {currentVideos.map((video) => (
                    <Col key={video.id} xs={6} lg={4}>
                      <div className="tutorials-video-item">
                        <TutorialsCardVideo url={`https://www.youtube.com/watch?v=${video.id}`} />
                        <p className="video-item-title">{video.title}</p>
                      </div>
                    </Col>
                  ))}
                  {Array.from({ length: getPlaceholderCount(currentVideos.length, columns) }).map((_, i) => (
                    <Col key={`coming-soon-${startIndex + currentVideos.length + i}`} xs={6} lg={4}>
                      <div className="tutorials-video-item tutorials-coming-soon">
                        <div className="coming-soon-thumb">
                          <FiClock size={26} />
                        </div>
                        <p className="coming-soon-label">{tempedgeTranslator('com.tempedge.msg.public.tutorials.comingsoon.label')}</p>
                        <p className="coming-soon-sub">{tempedgeTranslator('com.tempedge.msg.public.tutorials.comingsoon.sub')}</p>
                      </div>
                    </Col>
                  ))}
                </Row>
              )}

              {!isDocumentsTab && totalPages > 1 && (
                <div className="tut-paginator">
                  <PaginatorButtons changePage={handlePageChange} totalPages={totalPages} currentPage={activePage} />
                </div>
              )}
            </main>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default TutorialsSection;
